import { mdiDownload, mdiFileDocument } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useRef, useState } from "react";
import Papa from "papaparse";
import { OrderItemModelV2 } from "../../../../../service/models/orders/order_item_model";

export interface ItemBulkValidateResult {
  success: boolean;
  data?: OrderItemModelV2;
  name?: string;
  error?: string;
}

interface SuccessRow {
  row: number;
  item_code: string;
  name?: string;
  qty: number;
  price: number;
  data: OrderItemModelV2;
}

interface FailedRow {
  row: number;
  item_code: string;
  qty: string;
  price: string;
  error: string;
}

interface ItemBulkUploadProps {
  title: string;
  onClose: () => void;
  onComplete: (items: OrderItemModelV2[]) => void;
  validateRow: (
    item_code: string,
    qty: number,
    price: number,
  ) => Promise<ItemBulkValidateResult>;
  id_prefix?: string;
}

const TEMPLATE_CSV = "Item Code,Quantity,Price";

const normalizeHeader = (h: string) => {
  const k = h.trim().toLowerCase().replace(/\s+/g, "");
  if (k === "itemcode") return "item_code";
  if (k === "quantity" || k === "qty") return "qty";
  if (k === "price") return "price";
  return k;
};

const ItemBulkUpload: React.FC<ItemBulkUploadProps> = ({
  title,
  onClose,
  onComplete,
  validateRow,
  id_prefix = "item-bulk-upload",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [successRows, setSuccessRows] = useState<SuccessRow[] | null>(null);
  const [failedRows, setFailedRows] = useState<FailedRow[]>([]);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "order_items_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith(".csv")) {
      alert("Please upload a CSV file");
      return;
    }
    setUploading(true);
    setSuccessRows(null);
    setFailedRows([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: normalizeHeader,
      complete: async (results) => {
        await processCSVData(results.data as any[]);
      },
      error: (error) => {
        console.error("CSV parsing error:", error);
        alert("Error parsing CSV file");
        setUploading(false);
      },
    });
  };

  const processCSVData = async (data: any[]) => {
    const success: SuccessRow[] = [];
    const failed: FailedRow[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const rowNumber = i + 2; // header row + 0-index
      const itemCode = String(row.item_code ?? "").trim();
      const qtyRaw = String(row.qty ?? "").trim();
      const priceRaw = String(row.price ?? "").trim();

      // Trim empty rows: skip any row without an item code entirely
      // (don't surface it as a failed row).
      if (!itemCode) {
        continue;
      }

      const qty = Number(qtyRaw);
      if (
        !qtyRaw ||
        !Number.isFinite(qty) ||
        qty <= 0 ||
        !Number.isInteger(qty)
      ) {
        failed.push({
          row: rowNumber,
          item_code: itemCode,
          qty: qtyRaw,
          price: priceRaw,
          error: "Quantity must be a positive whole number",
        });
        continue;
      }

      let price = 0;
      if (priceRaw !== "") {
        price = Number(priceRaw);
        if (!Number.isFinite(price) || price < 0) {
          failed.push({
            row: rowNumber,
            item_code: itemCode,
            qty: qtyRaw,
            price: priceRaw,
            error: "Price must be a number greater than or equal to 0",
          });
          continue;
        }
      }

      const dupKey = itemCode.toLowerCase();
      if (seen.has(dupKey)) {
        failed.push({
          row: rowNumber,
          item_code: itemCode,
          qty: qtyRaw,
          price: priceRaw,
          error: "Duplicate item code in file",
        });
        continue;
      }
      seen.add(dupKey);

      try {
        const result = await validateRow(itemCode, qty, price);
        if (result.success && result.data) {
          success.push({
            row: rowNumber,
            item_code: itemCode,
            name: result.name,
            qty,
            price,
            data: result.data,
          });
        } else {
          failed.push({
            row: rowNumber,
            item_code: itemCode,
            qty: qtyRaw,
            price: priceRaw,
            error: result.error || "Validation failed",
          });
        }
      } catch (error: any) {
        failed.push({
          row: rowNumber,
          item_code: itemCode,
          qty: qtyRaw,
          price: priceRaw,
          error: error?.message || "Unknown error occurred",
        });
      }
    }

    setSuccessRows(success);
    setFailedRows(failed);
    setUploading(false);
  };

  const handleConfirm = () => {
    if (successRows && successRows.length > 0) {
      onComplete(successRows.map((r) => r.data));
      onClose();
    }
  };

  const hasResult = successRows !== null;

  return (
    <div
      id={`${id_prefix}-modal`}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 flex items-center justify-between">
          <h2
            id={`${id_prefix}-title`}
            className="text-sm md:text-xl font-semibold text-gray-800"
          >
            {title}
          </h2>
          <button
            id={`${id_prefix}-close-btn`}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 md:p-6 text-xs md:text-sm">
          {!hasResult ? (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 text-xs md:text-sm text-blue-800">
                CSV columns:{" "}
                <span className="font-semibold">
                  Item Code, Quantity, Price
                </span>{" "}
                (Price is optional). Only items belonging to this category are
                accepted.
              </div>

              {/* Template Download */}
              <div className="flex justify-center">
                <button
                  id={`${id_prefix}-download-template-btn`}
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 bg-myPrimaryColor text-white px-4 py-2 md:px-6 md:py-3 rounded-lg hover:bg-green-700 transition-colors shadow-md text-xs md:text-sm"
                >
                  <Icon path={mdiDownload} className="h-4 w-4 md:h-5 md:w-5" />
                  Download Sample Template
                </button>
              </div>

              {/* Upload Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 md:p-12 text-center transition-colors ${
                  dragActive
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 bg-gray-50"
                }`}
              >
                <input
                  id={`${id_prefix}-file-input`}
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Icon
                  path={mdiFileDocument}
                  className="h-10 w-10 md:h-16 md:w-16 mx-auto text-gray-400 mb-4"
                />
                <p className="text-gray-600 mb-2 text-xs md:text-sm">
                  Drag and drop your CSV file here, or
                </p>
                <button
                  id={`${id_prefix}-browse-btn`}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-myPrimaryColor text-white px-4 py-2 md:px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm"
                >
                  {uploading ? "Processing..." : "Browse Files"}
                </button>
              </div>

              {uploading && (
                <div className="flex items-center justify-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600 text-xs md:text-sm">
                    Processing CSV file...
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
                  <p className="text-xs md:text-sm text-green-700 mb-1">
                    Success
                  </p>
                  <p
                    id={`${id_prefix}-success-count`}
                    className="text-xl md:text-3xl font-bold text-green-900"
                  >
                    {successRows?.length ?? 0}
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 md:p-4">
                  <p className="text-xs md:text-sm text-red-700 mb-1">Failed</p>
                  <p
                    id={`${id_prefix}-failed-count`}
                    className="text-xl md:text-3xl font-bold text-red-900"
                  >
                    {failedRows.length}
                  </p>
                </div>
              </div>

              {/* Failed Rows */}
              {failedRows.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-red-900 text-xs md:text-sm">
                    Failed Rows:
                  </h3>
                  <div className="max-h-64 overflow-auto border border-red-200 rounded-lg">
                    <table className="min-w-full divide-y divide-red-200">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Row
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Item Code
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Qty
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Price
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Error
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-red-100">
                        {failedRows.map((row, index) => (
                          <tr
                            key={index}
                            id={`${id_prefix}-failed-row-${index}`}
                            className="hover:bg-red-50"
                          >
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.row}
                            </td>
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.item_code}
                            </td>
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.qty}
                            </td>
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.price}
                            </td>
                            <td className="px-3 py-2 text-xs md:text-sm text-red-600">
                              {row.error}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Success Rows Preview */}
              {successRows && successRows.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-green-900 text-xs md:text-sm">
                    Successfully Validated Rows ({successRows.length}):
                  </h3>
                  <div className="max-h-64 overflow-auto border border-green-200 rounded-lg">
                    <table className="min-w-full divide-y divide-green-200">
                      <thead className="bg-green-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Row
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Item Code
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Qty
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Price
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-green-100">
                        {successRows.map((row, index) => (
                          <tr
                            key={index}
                            id={`${id_prefix}-success-row-${index}`}
                            className="hover:bg-green-50"
                          >
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.row}
                            </td>
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.item_code}
                            </td>
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.name ?? "--"}
                            </td>
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.qty}
                            </td>
                            <td className="px-3 py-2 text-xs md:text-sm">
                              {row.price}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 md:px-6 md:py-4 border-t border-gray-200 flex justify-end gap-3">
          {hasResult && (
            <>
              <button
                id={`${id_prefix}-upload-again-btn`}
                onClick={() => {
                  setSuccessRows(null);
                  setFailedRows([]);
                }}
                className="px-4 py-2 md:px-6 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs md:text-sm"
              >
                Upload Again
              </button>
              <button
                id={`${id_prefix}-confirm-btn`}
                onClick={handleConfirm}
                disabled={!successRows || successRows.length === 0}
                className="px-4 py-2 md:px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm"
              >
                Confirm &amp; Add {successRows?.length ?? 0} Items
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemBulkUpload;
